import { useMemo, type ReactElement } from "react";
import { TrendingDown, TrendingUp } from "lucide-react";
import { Skeleton } from "@sphinx/design-system/skeleton";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { cn } from "@/lib/utils";
import { Area, AreaChart, Bar, BarChart, CartesianGrid, LabelList, XAxis, YAxis } from "recharts";
import type { DotProps } from "recharts";
import {
  evalSurfaceCardClass,
  evalSurfaceHeaderClass,
} from "./eval-surface-chrome";

const modelChartConfig = {
  passRate: {
    label: "Pass Rate",
    color: "var(--chart-1)",
  },
};

type TrendDelta = {
  value: number | null;
  label: string;
  colorClass: string;
};

function computeTrendDelta(
  data: Array<{ passRate: number }>,
): TrendDelta {
  if (data.length === 0) {
    return { value: null, label: "—", colorClass: "text-muted-foreground" };
  }
  if (data.length < 2) {
    return { value: null, label: "First run", colorClass: "text-muted-foreground" };
  }
  const delta =
    data[data.length - 1].passRate - data[data.length - 2].passRate;
  if (delta === 0) {
    return { value: 0, label: "No change", colorClass: "text-muted-foreground" };
  }
  return {
    value: delta,
    label: `${delta > 0 ? "+" : ""}${delta}% vs previous`,
    colorClass:
      delta > 0
        ? "bg-success/50 text-foreground"
        : "bg-destructive/50 text-foreground",
  };
}

/**
 * Pick which sparkline points get a "%" label printed above them.
 *
 * The old rule was "annotate every dot when there are ≤10 runs," but that
 * spammed identical labels on flat trends ("25% 25% 25% 25% …"). We now
 * always show first + last, and add the run-wide min/max only when they
 * actually differ from those endpoints — so a perfectly flat trend shows
 * exactly two labels and a varying trend still surfaces its extremes.
 */
function sparklineAnnotatedIndices(data: Array<{ passRate: number }>): Set<number> {
  if (data.length === 0) return new Set();
  if (data.length === 1) return new Set([0]);

  const indices = new Set<number>([0, data.length - 1]);
  let minIndex = 0;
  let maxIndex = 0;
  for (let index = 1; index < data.length; index += 1) {
    if (data[index].passRate < data[minIndex].passRate) minIndex = index;
    if (data[index].passRate > data[maxIndex].passRate) maxIndex = index;
  }
  const firstValue = data[0].passRate;
  const lastValue = data[data.length - 1].passRate;
  if (data[minIndex].passRate !== firstValue && data[minIndex].passRate !== lastValue) {
    indices.add(minIndex);
  }
  if (data[maxIndex].passRate !== firstValue && data[maxIndex].passRate !== lastValue) {
    indices.add(maxIndex);
  }
  return indices;
}

function SparklinePointLabel({
  x,
  y,
  value,
  index,
  annotatedIndices,
}: {
  // Recharts' LabelList content widens these to string | number from its
  // SVG prop types; we only annotate numerics, so guard and skip otherwise.
  x?: number | string;
  y?: number | string;
  value?: number | string;
  index?: number;
  annotatedIndices: Set<number>;
}) {
  if (
    typeof x !== "number" ||
    typeof y !== "number" ||
    value == null ||
    index == null ||
    !annotatedIndices.has(index)
  ) {
    return null;
  }

  return (
    <text
      x={x}
      y={y - 10}
      textAnchor="middle"
      fill="hsl(var(--muted-foreground))"
      fontSize={9}
      fontWeight={600}
    >
      {value}%
    </text>
  );
}

function createSparklineDot(
  pointCount: number,
  onRunClick?: (runId: string) => void,
) {
  return function SparklineDot(
    props: DotProps & { index?: number; payload?: { runId?: string } },
  ): ReactElement<SVGElement> {
    const { cx, cy, index, payload } = props;
    // Recharts' AreaDot type requires the function to return an SVGElement;
    // a no-op <g/> for the missing-coordinate case keeps the type honest.
    if (cx == null || cy == null) return <g />;

    const isLatest = index === pointCount - 1;
    const cursor = onRunClick ? "pointer" : undefined;

    return (
      <circle
        cx={cx}
        cy={cy}
        r={isLatest ? 4 : 3}
        fill="var(--color-passRate)"
        stroke="hsl(var(--background))"
        strokeWidth={2}
        style={{ cursor }}
        onClick={
          onRunClick && payload?.runId
            ? (event) => {
                event.stopPropagation();
                onRunClick(payload.runId!);
              }
            : undefined
        }
      />
    );
  };
}

function TrendDeltaBadge({ delta }: { delta: TrendDelta }) {
  if (delta.value === null || delta.value === 0) return null;

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-xs font-medium tabular-nums",
        delta.colorClass,
      )}
    >
      {delta.value !== null && delta.value !== 0 ? (
        delta.value > 0 ? (
          <TrendingUp className="h-3 w-3" aria-hidden />
        ) : (
          <TrendingDown className="h-3 w-3" aria-hidden />
        )
      ) : null}
      {delta.label}
    </span>
  );
}

type JudgeTrendPoint = {
  runId: string;
  runIdDisplay: string;
  label: string;
  judgeScore: number;
  judgeOffConfig: boolean;
};

/**
 * Advisory judge score across runs — the LLM's opinion, kept in its OWN card
 * rather than overlaid on the deterministic pass-rate chart, so a 0.6 judge
 * score and a failed check never read as the same kind of fact.
 *
 * Unjudged runs are dropped, not zeroed. Runs graded under a per-run judge
 * override wear the documented off-config glyph (⚙) so divergence from the
 * suite's judge calibration is visible, not silent.
 *
 * Renders nothing when no run in the window carries a judge verdict.
 */
function JudgeScoreCard({
  points,
  onRunClick,
}: {
  points: JudgeTrendPoint[];
  onRunClick?: (runId: string) => void;
}) {
  const latest = points.at(-1);
  const delta = useMemo(
    () =>
      computeTrendDelta(points.map((p) => ({ passRate: p.judgeScore }))),
    [points],
  );
  if (!latest) return null;

  return (
    <div className={evalSurfaceCardClass} data-testid="suite-judge-trend">
      <div className={cn(evalSurfaceHeaderClass, "rounded-t-2xl px-3 py-1.5")}>
        <div className="text-xs font-semibold tracking-tight text-foreground">
          Judge score
          <span className="ml-1.5 font-normal text-muted-foreground">
            advisory
          </span>
        </div>
      </div>
      <div className="px-3 pb-3 pt-2">
        <div className="flex items-center gap-4 sm:gap-6">
          <div className="flex shrink-0 flex-col">
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-semibold tabular-nums leading-none tracking-tight text-foreground">
                {latest.judgeScore}%
              </span>
              <TrendDeltaBadge delta={delta} />
            </div>
            <span className="mt-1 text-xs tabular-nums text-muted-foreground">
              {latest.judgeOffConfig ? "⚙ judge override · " : ""}
              latest judged run
            </span>
          </div>

          {points.length > 1 ? (
            <div className="flex min-w-0 flex-1 flex-col justify-end">
              <span className="mb-1 text-[10px] text-muted-foreground">
                Last {points.length} judged runs
              </span>
              <ChartContainer
                config={{
                  judgeScore: {
                    label: "Judge score",
                    color: "var(--chart-2)",
                  },
                }}
                className="aspect-auto h-16 w-full"
              >
                <AreaChart
                  data={points}
                  margin={{ top: 20, right: 6, left: 6, bottom: 2 }}
                  onClick={
                    onRunClick
                      ? (chartData: {
                          activePayload?: Array<{
                            payload?: { runId?: string };
                          }>;
                        }) => {
                          const runId =
                            chartData?.activePayload?.[0]?.payload?.runId;
                          if (runId) onRunClick(runId);
                        }
                      : undefined
                  }
                >
                  <XAxis
                    dataKey="runIdDisplay"
                    hide
                    padding={{ left: 8, right: 8 }}
                  />
                  <YAxis
                    hide
                    domain={[
                      (min: number) => Math.max(0, min - 12),
                      (max: number) => Math.min(100, max + 12),
                    ]}
                  />
                  <ChartTooltip
                    cursor={false}
                    content={
                      <ChartTooltipContent
                        labelFormatter={(_, payload) => {
                          const point = payload?.[0]?.payload as
                            | JudgeTrendPoint
                            | undefined;
                          const base =
                            point?.label ?? point?.runIdDisplay ?? "Run";
                          return point?.judgeOffConfig
                            ? `${base} · ⚙ judge override`
                            : base;
                        }}
                      />
                    }
                  />
                  <Area
                    type="monotone"
                    dataKey="judgeScore"
                    stroke="var(--color-judgeScore)"
                    fill="var(--color-judgeScore)"
                    fillOpacity={0.12}
                    strokeWidth={2}
                    isAnimationActive={false}
                    dot={<JudgeDot pointCount={points.length} />}
                    activeDot={
                      onRunClick
                        ? { cursor: "pointer", r: 5, strokeWidth: 2 }
                        : { r: 5, strokeWidth: 2 }
                    }
                  />
                </AreaChart>
              </ChartContainer>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/**
 * Judge-trend dot: off-config runs render the ⚙ glyph above the point so an
 * off-calibration grade can't hide in the line.
 */
function JudgeDot(
  props: DotProps & {
    index?: number;
    pointCount?: number;
    payload?: { judgeOffConfig?: boolean };
  },
): ReactElement<SVGElement> {
  const { cx, cy, index, pointCount, payload } = props;
  if (cx == null || cy == null) return <g />;
  const isLatest = index === (pointCount ?? 0) - 1;
  return (
    <g>
      <circle
        cx={cx}
        cy={cy}
        r={isLatest ? 4 : 3}
        fill="var(--color-judgeScore)"
        stroke="hsl(var(--background))"
        strokeWidth={2}
      />
      {payload?.judgeOffConfig ? (
        <text
          x={cx}
          y={cy - 8}
          textAnchor="middle"
          fontSize={9}
          fill="hsl(var(--muted-foreground))"
        >
          ⚙
        </text>
      ) : null}
    </g>
  );
}

const PLACEHOLDER_TREND_DATA = [42, 55, 60, 71, 78, 85, 91].map(
  (passRate, index) => ({
    runId: `placeholder-${index + 1}`,
    runIdDisplay: String(index + 1),
    passRate,
    label: `Sample run ${index + 1}`,
  }),
);

function SuiteAccuracyPlaceholder({ metricLabel }: { metricLabel: string }) {
  return (
    <div
      aria-hidden
      className="flex items-stretch gap-5 sm:gap-8"
      data-testid="suite-accuracy-placeholder"
    >
      <div className="flex shrink-0 flex-col justify-center">
        <span className="text-3xl font-semibold tabular-nums tracking-tight text-muted-foreground/60">
          —%
        </span>
        <span className="mt-0.5 text-xs text-muted-foreground">
          Latest {metricLabel.toLowerCase()}
        </span>
        <span className="mt-2 text-xs text-muted-foreground">
          Run the suite to see your trend
        </span>
      </div>

      <div className="relative flex min-w-0 flex-1 flex-col justify-end">
        <span className="mb-1.5 text-[10px] uppercase tracking-wide text-muted-foreground">
          Preview
        </span>
        <ChartContainer
          config={{
            passRate: {
              label: metricLabel,
              color: "hsl(var(--muted-foreground))",
            },
          }}
          className="aspect-auto h-24 w-full opacity-50"
        >
          <AreaChart
            data={PLACEHOLDER_TREND_DATA}
            margin={{ top: 20, right: 6, left: 6, bottom: 2 }}
          >
            <XAxis
              dataKey="runIdDisplay"
              hide
              padding={{ left: 8, right: 8 }}
            />
            <YAxis hide domain={[0, 100]} />
            <Area
              type="monotone"
              dataKey="passRate"
              stroke="var(--color-passRate)"
              fill="var(--color-passRate)"
              fillOpacity={0.1}
              strokeWidth={2}
              strokeDasharray="4 3"
              isAnimationActive={false}
              dot={false}
              activeDot={false}
            />
          </AreaChart>
        </ChartContainer>
      </div>
    </div>
  );
}

export interface SuiteRunsChartGridProps {
  suiteSource?: "ui" | "sdk";
  runTrendData: Array<{
    runId: string;
    runIdDisplay: string;
    passRate: number;
    passed?: number;
    total?: number;
    label: string;
    /**
     * Run-level advisory judge score (0-100 mean of per-case goal-completion
     * scores), or null/absent when the run was never judged. Null points are
     * DROPPED from the judge trend, never plotted as zero.
     */
    judgeScore?: number | null;
    /** Ran under a per-run judge override — marked with the off-config glyph. */
    judgeOffConfig?: boolean;
  }>;
  modelStats: Array<{
    model: string;
    passRate: number;
    passed: number;
    failed: number;
    total: number;
  }>;
  runsLoading: boolean;
  onRunClick?: (runId: string) => void;
}

export function SuiteRunsChartGrid({
  suiteSource,
  runTrendData,
  modelStats,
  runsLoading,
  onRunClick,
}: SuiteRunsChartGridProps) {
  const isSdk = suiteSource === "sdk";
  const metricLabel = isSdk ? "Pass rate" : "Accuracy";
  const showModelChart = modelStats.length > 1;
  // Unjudged runs are dropped, not zeroed — same honesty rule as the swarm
  // overview's daily trend.
  const judgePoints = useMemo(
    () =>
      runTrendData.flatMap((point) =>
        typeof point.judgeScore === "number"
          ? [
              {
                runId: point.runId,
                runIdDisplay: point.runIdDisplay,
                label: point.label,
                judgeScore: point.judgeScore,
                judgeOffConfig: point.judgeOffConfig === true,
              },
            ]
          : [],
      ),
    [runTrendData],
  );

  const latest = runTrendData.at(-1);
  const delta = useMemo(() => computeTrendDelta(runTrendData), [runTrendData]);
  const latestPassFailLabel = useMemo(() => {
    if (!latest || latest.total == null || latest.total <= 0) return null;
    const failed = Math.max(0, latest.total - (latest.passed ?? 0));
    return `${latest.passed ?? 0} passed · ${failed} failed`;
  }, [latest]);
  const annotatedIndices = useMemo(
    () => sparklineAnnotatedIndices(runTrendData),
    [runTrendData],
  );
  const SparklineDot = useMemo(
    () => createSparklineDot(runTrendData.length, onRunClick),
    [runTrendData.length, onRunClick],
  );

  return (
    <div
      className={cn(
        "grid gap-4",
        (showModelChart || judgePoints.length > 0) && "lg:grid-cols-2",
      )}
    >
      <div className={evalSurfaceCardClass}>
        <div
          className={cn(
            evalSurfaceHeaderClass,
            "rounded-t-2xl px-3 py-1.5",
          )}
        >
          <div className="text-xs font-semibold tracking-tight text-foreground">
            {isSdk ? "Pass rate" : "Suite accuracy"}
          </div>
        </div>
        <div className="px-3 pb-3 pt-2">
          {runsLoading ? (
            <Skeleton className="h-14 w-full" />
          ) : !latest ? (
            <SuiteAccuracyPlaceholder metricLabel={metricLabel} />
          ) : (
            <div className="flex items-center gap-4 sm:gap-6">
              <div className="flex shrink-0 flex-col">
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-semibold tabular-nums leading-none tracking-tight text-foreground">
                    {latest.passRate}%
                  </span>
                  <TrendDeltaBadge delta={delta} />
                </div>
                {latestPassFailLabel ? (
                  <span className="mt-1 text-xs tabular-nums text-muted-foreground">
                    {latestPassFailLabel}
                  </span>
                ) : null}
              </div>

              {runTrendData.length > 1 ? (
                <div className="flex min-w-0 flex-1 flex-col justify-end">
                  <span className="mb-1 text-[10px] text-muted-foreground">
                    Last {runTrendData.length} runs
                  </span>
                  <ChartContainer
                    config={{
                      passRate: {
                        label: metricLabel,
                        color: "var(--chart-1)",
                      },
                    }}
                    className="aspect-auto h-16 w-full"
                  >
                  <AreaChart
                    data={runTrendData}
                    margin={{ top: 20, right: 6, left: 6, bottom: 2 }}
                    onClick={
                      onRunClick
                        ? (chartData: {
                            activePayload?: Array<{
                              payload?: { runId?: string };
                            }>;
                          }) => {
                            const runId =
                              chartData?.activePayload?.[0]?.payload?.runId;
                            if (runId) onRunClick(runId);
                          }
                        : undefined
                    }
                  >
                    <XAxis
                      dataKey="runIdDisplay"
                      hide
                      padding={{ left: 8, right: 8 }}
                    />
                    <YAxis
                      hide
                      domain={[
                        (min: number) => Math.max(0, min - 12),
                        (max: number) => Math.min(100, max + 12),
                      ]}
                    />
                    <ChartTooltip
                      cursor={false}
                      content={
                        <ChartTooltipContent
                          labelFormatter={(_, payload) => {
                            const point = payload?.[0]?.payload as
                              | { label?: string; runIdDisplay?: string }
                              | undefined;
                            return (
                              point?.label ??
                              point?.runIdDisplay ??
                              "Run"
                            );
                          }}
                        />
                      }
                    />
                    <Area
                      type="monotone"
                      dataKey="passRate"
                      stroke="var(--color-passRate)"
                      fill="var(--color-passRate)"
                      fillOpacity={0.12}
                      strokeWidth={2}
                      isAnimationActive={false}
                      dot={SparklineDot}
                      activeDot={
                        onRunClick
                          ? { cursor: "pointer", r: 5, strokeWidth: 2 }
                          : { r: 5, strokeWidth: 2 }
                      }
                    >
                      <LabelList
                        dataKey="passRate"
                        content={(props) => (
                          <SparklinePointLabel
                            {...props}
                            annotatedIndices={annotatedIndices}
                          />
                        )}
                      />
                    </Area>
                  </AreaChart>
                </ChartContainer>
                </div>
              ) : null}
            </div>
          )}
        </div>
      </div>

      {judgePoints.length > 0 ? (
        <JudgeScoreCard points={judgePoints} onRunClick={onRunClick} />
      ) : null}

      {showModelChart ? (
        <div className={evalSurfaceCardClass}>
          <div
            className={cn(
              evalSurfaceHeaderClass,
              "rounded-t-2xl px-4 py-2.5",
            )}
          >
            <div className="text-xs font-semibold tracking-tight text-foreground">
              Performance by model
            </div>
          </div>
          <div className="px-4 pb-4">
            <ChartContainer
              config={modelChartConfig}
              className="aspect-auto h-32 w-full"
            >
              <BarChart data={modelStats} width={undefined} height={undefined}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={false}
                  stroke="hsl(var(--muted-foreground) / 0.2)"
                />
                <XAxis
                  dataKey="model"
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                  tick={{ fontSize: 11 }}
                  interval={0}
                  height={40}
                  tickFormatter={(value: string) => {
                    if (value.length > 15) {
                      return `${value.substring(0, 12)}...`;
                    }
                    return value;
                  }}
                />
                <YAxis
                  domain={[0, 100]}
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                  tick={{ fontSize: 12 }}
                  tickFormatter={(value: number) => `${value}%`}
                />
                <ChartTooltip
                  cursor={false}
                  content={({ active, payload }) => {
                    if (!active || !payload || payload.length === 0)
                      return null;
                    const data = payload[0].payload as {
                      model: string;
                      passRate: number;
                      passed: number;
                      failed: number;
                    };
                    return (
                      <div className="rounded-lg border bg-background p-2 shadow-sm">
                        <div className="grid gap-2">
                          <div className="flex flex-col">
                            <span className="text-xs font-semibold">
                              {data.model}
                            </span>
                            <span className="mt-0.5 text-xs text-muted-foreground">
                              {data.passed} passed · {data.failed} failed
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div
                              className="h-2 w-2 rounded-full"
                              style={{
                                backgroundColor: "var(--color-passRate)",
                              }}
                            />
                            <span className="text-sm font-semibold">
                              {data.passRate}%
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  }}
                />
                <Bar
                  dataKey="passRate"
                  fill="var(--color-passRate)"
                  radius={[4, 4, 0, 0]}
                  isAnimationActive={false}
                  minPointSize={8}
                />
              </BarChart>
            </ChartContainer>
          </div>
        </div>
      ) : null}
    </div>
  );
}
